import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  format,
  getDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
// import moment from "moment-hijri";

interface ArabicCalendarProps {
  value?: Date;
  onChange?: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Arabic-Indic numerals conversion
const toArabicIndic = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) {
    return "٠";
  }
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return num
    .toString()
    .split("")
    .map((digit) => arabicNumerals[parseInt(digit)] || digit)
    .join("");
};

const arabicMonths = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const hijriMonths = [
  "محرم",
  "صفر",
  "ربيع الأول",
  "ربيع الثاني",
  "جمادى الأولى",
  "جمادى الآخرة",
  "رجب",
  "شعبان",
  "رمضان",
  "شوال",
  "ذو القعدة",
  "ذو الحجة",
];

const arabicDaysShort = ["ح", "ن", "ث", "ر", "خ", "ج", "س"];

// خوارزمية تحويل موثوقة من Kuwait Calendar
function gmod(n: number, m: number) {
  return ((n % m) + m) % m;
}
function asciiDigits(s: string) {
  // convert Arabic-Indic (U+0660..U+0669) and Eastern Arabic-Indic (U+06F0..U+06F9)
  if (!s) return s;
  const map: Record<number, string> = {};
  for (let i = 0; i <= 9; i++) {
    map[0x0660 + i] = String(i); // ٠-٩
    map[0x06f0 + i] = String(i); // ۰-۹
  }
  let out = "";
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (map[code] !== undefined) out += map[code];
    else out += ch;
  }
  return out;
}
function convertToHijri(date: Date) {
  try {
    const formatter = new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });

    const yyyy = date.getFullYear();
    const mm = date.getMonth();
    const dd = date.getDate();


    // Create a clean date with no time
    const pureDate = new Date(Date.UTC(yyyy, mm, dd));

    // Now convert the *pure date*
    const parts = formatter.formatToParts(pureDate);
    
    // console.log("parts", parts);
    const day = parseInt(
      asciiDigits(parts.find((p) => p.type === "day")?.value || "0"),
      10
    );
    // console.log("day", day);
    const month =
      parseInt(
        asciiDigits(parts.find((p) => p.type === "month")?.value || "0"),
        10
      ) - 1; // 0-based for JS consistency
    const year = parseInt(
      asciiDigits(parts.find((p) => p.type === "year")?.value || "0"),
      10
    );


    if (day && month >= 0 && year) {
      return { day, month, year };
    }
  } catch (e) {
    console.error(e);
  }

  // ❌ Fallback to manual conversion (your algorithm)
  const day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  if (month < 3) {
    year -= 1;
    month += 12;
  }

  const a = Math.floor(year / 100);
  let b = 2 - a + Math.floor(a / 4);

  if (year < 1583) b = 0;
  if (year === 1582) {
    if (month > 10) b = -10;
    if (month === 10 && day > 4) b = -10;
  }

  const jd =
    Math.floor(365.25 * (year + 4716)) +
    Math.floor(30.6001 * (month + 1)) +
    day +
    b -
    1524;

  const iyear = 10631 / 30;
  const epochastro = 1948084;
  const shift1 = 8.01 / 60;

  let z = jd - epochastro;
  const cyc = Math.floor(z / 10631);
  z -= 10631 * cyc;
  const j = Math.floor((z - shift1) / iyear);
  const iy = 30 * cyc + j;
  z -= Math.floor(j * iyear + shift1);
  let im = Math.floor((z + 28.5001) / 29.5);
  if (im === 13) im = 12;
  const id = z - Math.floor(29.5001 * im - 29);

  return {
    day: Math.floor(id),
    month: im - 1, // 0-based month
    year: iy,
  };
}

const gregorianToHijri = convertToHijri;

export function ArabicCalendar({
  value,
  onChange,
  placeholder = "اختر التاريخ",
  disabled = false,
  className,
}: ArabicCalendarProps) {
  // استخدام التاريخ الحقيقي دائماً
  const [currentMonth, setCurrentMonth] = React.useState(() => new Date());
  const [isOpen, setIsOpen] = React.useState(false);
  const [calendarType, setCalendarType] = React.useState<"gregorian" | "hijri">(
    "hijri"
  );

  // تحديث الشهر الحالي إلى تاريخ اليوم عند فتح التقويم
  React.useEffect(() => {
    if (isOpen) {
      if (value) {
        setCurrentMonth(value);
      } else {
        const today = new Date();
        setCurrentMonth(today);
        if (onChange) {
          onChange(today);
        }
      }
    }
  }, [isOpen]);

  // Generate calendar days based on calendar type
  let daysInMonth: Date[];
  let emptyDays: number[];
  let firstDayOfWeek: number;

  if (calendarType === "hijri") {
    // للتقويم الهجري، نحتاج لحساب الأيام بشكل مختلف
    const currentHijri = gregorianToHijri(currentMonth);
    const hijriMonthDays = [30, 29, 30, 29, 30, 29, 30, 29, 30, 29, 30, 29];
    const daysInHijriMonth = hijriMonthDays[currentHijri.month];

    // نجد أول يوم في الشهر الهجري (تقريبي)
    let firstGregorianDay = currentMonth;
    let firstHijriCheck = gregorianToHijri(firstGregorianDay);

    // نتراجع حتى نجد بداية الشهر الهجري
    while (
      firstHijriCheck.day !== 1 ||
      firstHijriCheck.month !== currentHijri.month
    ) {
      firstGregorianDay = new Date(
        firstGregorianDay.getTime() - 24 * 60 * 60 * 1000
      );
      firstHijriCheck = gregorianToHijri(firstGregorianDay);

      // حماية من الحلقة اللانهائية
      if (
        Math.abs(firstGregorianDay.getTime() - currentMonth.getTime()) >
        45 * 24 * 60 * 60 * 1000
      ) {
        break;
      }
    }

    // نولد الأيام للشهر الهجري
    daysInMonth = [];
    for (let i = 0; i < daysInHijriMonth; i++) {
      const day = new Date(firstGregorianDay);
      day.setDate(firstGregorianDay.getDate() + i);
      daysInMonth.push(day);
    }

    firstDayOfWeek = getDay(firstGregorianDay);
    emptyDays = Array.from({ length: firstDayOfWeek }, (_, i) => i);
  } else {
    // للتقويم الميلادي (المنطق الأصلي)
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    firstDayOfWeek = getDay(monthStart);
    emptyDays = Array.from({ length: firstDayOfWeek }, (_, i) => i);
  }

  // Format date display
  const formatDateDisplay = (date: Date) => {
    if (!date || isNaN(date.getTime())) {
      return placeholder;
    }

    if (calendarType === "hijri") {
      const hijri = gregorianToHijri(date);
      const day = toArabicIndic(hijri.day);
      const month = toArabicIndic(hijri.month + 1);
      const year = toArabicIndic(hijri.year);
      return `${day} / ${month} / ${year} هـ`;
    } else {
      const day = toArabicIndic(date.getDate());
      const month = toArabicIndic(date.getMonth() + 1);
      const year = toArabicIndic(date.getFullYear());
      return `${day} / ${month} / ${year} م`;
    }
  };

  const handleDateSelect = (date: Date) => {
    onChange?.(date);
    setIsOpen(false);
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const toggleCalendarType = () => {
    setCalendarType((prev) => (prev === "gregorian" ? "hijri" : "gregorian"));
  };

  const getCurrentMonthDisplay = () => {
    if (calendarType === "hijri") {
      const hijri = gregorianToHijri(currentMonth);
      const monthName = hijriMonths[hijri.month] || hijriMonths[0];
      const year = toArabicIndic(hijri.year);
      return `${monthName} ${year} هـ`;
    } else {
      const monthName = arabicMonths[currentMonth.getMonth()];
      const year = toArabicIndic(currentMonth.getFullYear());
      return `${monthName} ${year} م`;
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-right font-normal h-10 px-3",
            !value && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
          dir="rtl"
        >
          <CalendarIcon className="ml-2 h-4 w-4" />
          {value ? formatDateDisplay(value) : formatDateDisplay(new Date())}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-white shadow-xl border border-gray-200 rounded-lg pointer-events-auto"
        align="start"
        dir="rtl"
      >
        <div className="p-4 space-y-4">
          {/* Calendar Type Selector */}
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={toggleCalendarType}
              className={cn(
                "px-4 py-2 text-sm rounded-full transition-colors",
                calendarType === "hijri"
                  ? "bg-teal-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              )}
            >
              هجري
            </button>
            <button
              onClick={toggleCalendarType}
              className={cn(
                "px-4 py-2 text-sm rounded-full transition-colors",
                calendarType === "gregorian"
                  ? "bg-teal-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              )}
            >
              ميلادي
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNextMonth}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">
                {getCurrentMonthDisplay()}
              </div>
              <div className="text-sm text-gray-500">
                {calendarType === "hijri"
                  ? "التقويم الهجري"
                  : "التقويم الميلادي"}
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={goToPreviousMonth}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {arabicDaysShort.map((day, index) => (
              <div
                key={index}
                className="h-8 w-8 flex items-center justify-center text-xs font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month starts */}
            {emptyDays.map((_, index) => (
              <div key={`empty-${index}`} className="h-8 w-8" />
            ))}

            {/* Days of the month */}
            {daysInMonth.map((day, index) => {
              const isSelected = value && isSameDay(day, value);
              const isTodayDate = isToday(day);

              // عرض التاريخ بناءً على نوع التقويم
              let dayDisplay;
              if (calendarType === "hijri") {
                // للتقويم الهجري، عرض رقم اليوم الهجري المباشر
                const currentHijri = gregorianToHijri(day);
                dayDisplay = toArabicIndic(currentHijri.day);
              } else {
                // للتقويم الميلادي العادي
                dayDisplay = toArabicIndic(day.getDate());
              }

              return (
                <button
                  key={`${day.getTime()}-${calendarType}-${index}`}
                  onClick={() => handleDateSelect(day)}
                  className={cn(
                    "h-8 w-8 text-sm font-medium rounded-md transition-colors",
                    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1",
                    isSelected &&
                      "bg-primary text-primary-foreground hover:bg-primary/90",
                    isTodayDate && !isSelected && "bg-gray-200 text-gray-900",
                    !isSelected && !isTodayDate && "text-gray-700"
                  )}
                >
                  {dayDisplay}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="pt-2 border-t border-gray-200">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full py-2 text-sm text-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              {placeholder}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
